import { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';
import type { ProductKeys3P as ProductKeys3PType } from '@atlassian/rovo-connectors-config/types';

export const SMARTLINK_CONNECTION_LINK_COUNT_TRAIT =
	'confluence_3p_smartlink_connection_link_count';

export const SMARTLINK_CONNECTION_LINK_COUNT_BY_TEAM_TYPE_TRAIT_PREFIX =
	'confluence_3p_smartlink_connection_link_count_by_team_type_';

const SUFFICIENCY_THRESHOLD = 20;
export const DEFAULT_MAX_RECOMMENDED_APPS = 3;

export type SmartlinkConnectorRecommendationInputs = {
	siteWideSmartlinkAppCounts?: string;
	teamTypeSmartlinkAppCounts?: string;
};

type ParsedAppCount = {
	appId: string;
	count: number;
};

const APP_ID_TO_PRODUCT_KEY_ALIASES: Record<string, ProductKeys3PType> = {
	drive: ProductKeys3P.GoogleDrive,
	figma: ProductKeys3P.Figma,
	github: ProductKeys3P.Github,
	'google-drive': ProductKeys3P.GoogleDrive,
	onedrive: ProductKeys3P.OneDrive,
	sharepoint: ProductKeys3P.Sharepoint,
	slack: ProductKeys3P.Slack,
};

export const parseAppCounts = (raw: string | undefined): ParsedAppCount[] => {
	if (!raw || raw.trim().length === 0) {
		return [];
	}

	return raw
		.split(',')
		.map((entry) => entry.trim())
		.filter((entry) => entry.length > 0)
		.map((entry) => {
			const separatorIndex = entry.indexOf(':');
			if (separatorIndex <= 0 || separatorIndex === entry.length - 1) {
				return undefined;
			}

			const appId = entry.slice(0, separatorIndex).trim();
			const count = Number(entry.slice(separatorIndex + 1).trim());

			if (appId.length === 0 || !Number.isFinite(count)) {
				return undefined;
			}

			return { appId, count };
		})
		.filter((entry): entry is ParsedAppCount => entry !== undefined);
};

export const topNonZeroAppsByCount = (parsed: ReadonlyArray<ParsedAppCount>): string[] =>
	parsed
		.map((entry, index) => ({ ...entry, index }))
		.filter((entry) => entry.count > 0)
		.sort((a, b) => {
			if (b.count !== a.count) {
				return b.count - a.count;
			}

			return a.index - b.index;
		})
		.map((entry) => entry.appId);

export const canonicalizeAppIdToProductKey = (appId: string): ProductKeys3PType | undefined => {
	return APP_ID_TO_PRODUCT_KEY_ALIASES[appId.toLowerCase()];
};

export const keepOnlySupportedProductKeys = (apps: ReadonlyArray<string>): ProductKeys3PType[] =>
	apps
		.map(canonicalizeAppIdToProductKey)
		.filter((productKey): productKey is ProductKeys3PType => productKey !== undefined);

export const dedupeMergeProductKeys = (
	existing: ReadonlyArray<ProductKeys3PType>,
	incoming: ReadonlyArray<ProductKeys3PType>,
	max: number,
): ProductKeys3PType[] => {
	const result: ProductKeys3PType[] = [];
	const seen = new Set<ProductKeys3PType>();

	for (const productKey of [...existing, ...incoming]) {
		if (!seen.has(productKey)) {
			seen.add(productKey);
			result.push(productKey);
			if (result.length >= max) {
				return result;
			}
		}
	}

	return result;
};

export const resolveRecommendedProductKeys = (
	{
		siteWideSmartlinkAppCounts,
		teamTypeSmartlinkAppCounts,
	}: SmartlinkConnectorRecommendationInputs = {},
	maxRecommendedApps: number = DEFAULT_MAX_RECOMMENDED_APPS,
): ProductKeys3PType[] => {
	let productKeys: ProductKeys3PType[] = [];

	const teamTypeParsed = parseAppCounts(teamTypeSmartlinkAppCounts);
	const supportedTeamTypeApps = teamTypeParsed.filter((entry) =>
		canonicalizeAppIdToProductKey(entry.appId),
	);
	const teamTypePositiveSum = supportedTeamTypeApps.reduce(
		(sum, entry) => sum + Math.max(0, entry.count),
		0,
	);

	if (teamTypePositiveSum >= SUFFICIENCY_THRESHOLD) {
		productKeys = dedupeMergeProductKeys(
			[],
			keepOnlySupportedProductKeys(topNonZeroAppsByCount(teamTypeParsed)),
			maxRecommendedApps,
		);
	}

	if (productKeys.length < maxRecommendedApps) {
		const siteWideParsed = parseAppCounts(siteWideSmartlinkAppCounts);
		productKeys = dedupeMergeProductKeys(
			productKeys,
			keepOnlySupportedProductKeys(topNonZeroAppsByCount(siteWideParsed)),
			maxRecommendedApps,
		);
	}

	return productKeys;
};
